import React, { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "react-toastify";
import { useMediaQuery, useTheme } from "@mui/material";
import { Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Slider, Typography } from "@mui/material";
import { ArrowBack, Close as CloseIcon, CloudUploadOutlined, CropSquare, RotateLeft, RotateRight } from "@mui/icons-material";

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const BLUE = "#2563EB";
const BLUE_HOVER = "#1d4ed8";
const BLUE_LIGHT = "rgba(37,99,235,0.1)";
const BLUE_XLIGHT = "rgba(37,99,235,0.06)";

// ─── Canvas helper ────────────────────────────────────────────────────────────
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

export async function getCroppedBlob(imageSrc, pixelCrop, rotation = 0, outputSize = 512, fileType = "image/jpeg") {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  ctx.translate(outputSize / 2, outputSize / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-outputSize / 2, -outputSize / 2);
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, outputSize, outputSize);
  return new Promise((res) => canvas.toBlob((b) => res(b), fileType, 0.92));
}

// ─── Step progress bar ────────────────────────────────────────────────────────
const StepBar = ({ step }) => (
  <Box sx={{ display: "flex", gap: "3px", px: 3 }}>
    {[1, 2].map((s) => (
      <Box
        key={s}
        sx={{
          flex: 1,
          height: "3px",
          borderRadius: "99px",
          bgcolor: s <= step ? BLUE : "rgba(37,99,235,0.15)",
          transition: "background-color 0.35s ease",
        }}
      />
    ))}
  </Box>
);

// ─── Shared buttons ───────────────────────────────────────────────────────────
const CancelBtn = ({ onClick, disabled }) => (
  <Button
    onClick={onClick}
    disabled={disabled}
    variant="outlined"
    size="small"
    sx={{
      borderRadius: "8px",
      textTransform: "none",
      fontWeight: 600,
      fontSize: "13px",
      px: 2.5,
      borderColor: "#d1d5db",
      color: "#6b7280",
      "&:hover": { borderColor: "#9ca3af", bgcolor: "#f9fafb", color: "#374151" },
      "&.Mui-disabled": { borderColor: "#e5e7eb", color: "#d1d5db" },
    }}
  >
    Cancel
  </Button>
);

const PrimaryBtn = ({ disabled, onClick, label = "Continue", loading = false }) => (
  <Button
    disabled={disabled || loading}
    onClick={onClick}
    variant="contained"
    size="small"
    disableElevation
    sx={{
      borderRadius: "8px",
      textTransform: "none",
      fontWeight: 700,
      fontSize: "13px",
      px: 2.5,
      bgcolor: BLUE,
      color: "#fff",
      "&:hover": { bgcolor: BLUE_HOVER },
      "&.Mui-disabled": { bgcolor: "rgba(37,99,235,0.25)", color: "rgba(255,255,255,0.5)" },
    }}
  >
    {loading && <CircularProgress size={13} sx={{ color: "#fff", mr: 1 }} />}
    {label}
  </Button>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export const ImageCropperModal = ({
  open,
  onClose,
  onSave,
  title = "Upload Image",
  cropTitle = "Position your image",
  aspectRatio = 1,
  outputSize = 512,
  cropShape = "rect",
  maxFileSizeMB = 2,
  dropHint = "Recommended: at least 200 × 200 px",
  saveLabel = "Save",
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [src, setSrc] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [fileName, setFileName] = useState("image.jpg");
  const [fileType, setFileType] = useState("image/jpeg");
  const fileInputRef = useRef(null);

  const step = src ? 2 : 1;
  const maxBytes = maxFileSizeMB * 1024 * 1024;

  const onCropComplete = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);

  const fileReaderRef = useRef(null);

  const processSelectedFile = (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast("Please upload a valid image file (JPG, PNG, GIF, SVG).", { type: "warning" });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (file.size > maxBytes) {
      toast(`Image must be smaller than ${maxFileSizeMB} MB`, { type: "warning" });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (fileReaderRef.current) {
      fileReaderRef.current.abort();
    }

    setFileName(file.name);
    setFileType(file.type);

    const reader = new FileReader();
    fileReaderRef.current = reader;

    reader.onloadend = () => {
      if (reader.result) {
        setSrc(reader.result);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
      }
      fileReaderRef.current = null;
    };
    reader.onerror = () => {
      toast("Unable to read image file. Please try another.", { type: "error" });
      fileReaderRef.current = null;
      if (fileInputRef.current) fileInputRef.current.value = "";
    };
    reader.readAsDataURL(file);
  };

  const handleClear = () => {
    setSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setFileName("image.jpg");
    setFileType("image/jpeg");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    handleClear();
    setLoading(false);
    onClose();
  };

  const handleSave = async () => {
    if (!src || !croppedAreaPixels) {
      toast("Please select and position an image first", { type: "info" });
      return;
    }
    try {
      setLoading(true);
      const blob = await getCroppedBlob(src, croppedAreaPixels, rotation, outputSize, fileType);
      await onSave(blob, fileName, fileType);
      handleClose();
    } catch (err) {
      console.error("ImageCropperModal ~ handleSave error:", err);
      toast("An error occurred while cropping the image.", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? handleClose : undefined}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : "20px",
          overflow: "hidden",
          boxShadow: fullScreen ? "none" : "0 24px 60px rgba(0,0,0,0.13)",
          bgcolor: "#fff",
          minHeight: step === 2 ? { xs: "100%", sm: "520px" } : "auto",
          maxHeight: fullScreen ? "100%" : "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          flexShrink: 0,
          px: 3,
          pt: 2.5,
          pb: 1.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          {step === 2 && (
            <IconButton
              size="small"
              onClick={handleClear}
              disabled={loading}
              aria-label="Go back to upload step"
              sx={{
                color: "text.secondary",
                "&:hover": { color: "text.primary", bgcolor: "action.hover" },
              }}
            >
              <ArrowBack sx={{ fontSize: 18 }} />
            </IconButton>
          )}

          {/* Icon badge */}
          <Box
            sx={{
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: "10px",
              bgcolor: BLUE_LIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {step === 2 ? (
              <CropSquare sx={{ fontSize: 18, color: BLUE }} />
            ) : (
              <CloudUploadOutlined sx={{ fontSize: 18, color: BLUE }} />
            )}
          </Box>

          {/* Text */}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              lineHeight={1.25}
              noWrap
              sx={{ letterSpacing: "-0.01em", color: "text.primary" }}
            >
              {step === 2 ? cropTitle : title}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", mt: 0.1, color: "text.disabled" }}>
              {step === 2 ? "Scroll to zoom · drag to reposition" : "PNG, JPG, SVG or GIF · max " + maxFileSizeMB + " MB"}
            </Typography>
          </Box>
        </Box>

        {/* Close */}
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={loading}
          aria-label="Close modal"
          sx={{
            mt: 0.25,
            flexShrink: 0,
            color: "text.secondary",
            "&:hover": { color: "text.primary", bgcolor: "action.hover" },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* ── Progress bar ── */}
      <StepBar step={step} />

      {/* ── Divider ── */}
      <Box sx={{ height: "1px", mx: 3, mt: 1.5, bgcolor: "divider", flexShrink: 0 }} />

      {/* ── Content ── */}
      <DialogContent
        sx={{
          p: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: "transparent",
        }}
      >
        {step === 1 ? (
          /* ── STEP 1: Drop Zone ── */
          <Box sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
            <Box
              role="button"
              tabIndex={0}
              aria-label="Upload image — click or press Enter to browse files"
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                processSelectedFile(e.dataTransfer.files?.[0]);
              }}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              sx={{
                flex: 1,
                minHeight: 240,
                border: "2px dashed",
                borderColor: dragOver ? BLUE : "#d1d5db",
                borderRadius: "14px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                bgcolor: dragOver ? BLUE_XLIGHT : "#f8fafc",
                cursor: "pointer",
                transition: "all 0.18s ease",
                userSelect: "none",
                "&:hover": {
                  borderColor: BLUE,
                  bgcolor: BLUE_XLIGHT,
                  "& .upload-icon": { transform: "translateY(-3px)" },
                },
                "&:focus-visible": {
                  outline: `2px solid ${BLUE}`,
                  outlineOffset: "2px",
                  borderColor: BLUE,
                  bgcolor: BLUE_XLIGHT,
                },
              }}
            >
              <Box
                className="upload-icon"
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  bgcolor: dragOver ? BLUE : BLUE_LIGHT,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "transform 0.2s ease, background 0.18s ease",
                }}
              >
                <CloudUploadOutlined sx={{ fontSize: 28, color: dragOver ? "#fff" : BLUE, transition: "color 0.18s" }} />
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body1" fontWeight={600} color="text.primary">
                  Drop your image here
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  or{" "}
                  <Typography component="span" variant="body2" fontWeight={600} sx={{ color: BLUE, textDecoration: "underline" }}>
                    browse files
                  </Typography>
                </Typography>
              </Box>

              <Typography variant="caption" color="text.disabled">
                {dropHint}
              </Typography>
            </Box>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={(e) => processSelectedFile(e.target.files?.[0])}
            />
          </Box>
        ) : (
          /* ── STEP 2: Crop ── */
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Dark canvas — the image stands out naturally, no border needed */}
            <Box sx={{ flex: 1, position: "relative", bgcolor: "#0f172a", overflow: "hidden" }}>
              <Cropper
                image={src}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspectRatio}
                cropShape={cropShape}
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                style={{
                  containerStyle: { position: "absolute", inset: 0 },
                  cropAreaStyle: {
                    border: "none",
                    boxShadow: "0 0 0 9999px rgba(0,0,0,0.75)",
                  },
                }}
              />
            </Box>

            {/* Rotation bar */}
            <Box
              sx={{
                flexShrink: 0,
                bgcolor: "#1e293b",
                borderTop: "1px solid rgba(255,255,255,0.07)",
                px: 2.5,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <IconButton
                size="small"
                onClick={() => setRotation((r) => Math.max(-180, r - 90))}
                aria-label="Rotate left 90 degrees"
                sx={{ color: "rgba(255,255,255,0.45)", "&:hover": { color: "#fff" } }}
              >
                <RotateLeft fontSize="small" />
              </IconButton>
              <Slider
                value={rotation}
                min={-180}
                max={180}
                step={1}
                onChange={(_, v) => setRotation(v)}
                size="small"
                sx={{
                  flex: 1,
                  color: BLUE,
                  "& .MuiSlider-thumb": {
                    width: 14,
                    height: 14,
                    bgcolor: "#fff",
                    "&:hover, &.Mui-focusVisible": { boxShadow: "0 0 0 6px rgba(37,99,235,0.3)" },
                  },
                  "& .MuiSlider-track": { bgcolor: BLUE, border: "none" },
                  "& .MuiSlider-rail": { bgcolor: "rgba(255,255,255,0.15)" },
                }}
              />
              <IconButton
                size="small"
                onClick={() => setRotation((r) => Math.min(180, r + 90))}
                aria-label="Rotate right 90 degrees"
                sx={{ color: "rgba(255,255,255,0.45)", "&:hover": { color: "#fff" } }}
              >
                <RotateRight fontSize="small" />
              </IconButton>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.35)",
                  fontVariantNumeric: "tabular-nums",
                  minWidth: 36,
                  textAlign: "right",
                  fontSize: "11px",
                }}
              >
                {rotation}°
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <Box
        sx={{
          flexShrink: 0,
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: step === 2 ? "space-between" : "flex-end",
          alignItems: "center",
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        {step === 2 && (
          <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "11px" }}>
            Scroll / pinch to zoom
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <CancelBtn onClick={handleClose} disabled={loading} />
          {step === 2 ? (
            <PrimaryBtn
              disabled={!src || loading}
              label={loading ? "Saving…" : saveLabel}
              onClick={handleSave}
              loading={loading}
            />
          ) : (
            <PrimaryBtn disabled={!src} label="Continue" />
          )}
        </Box>
      </Box>
    </Dialog>
  );
};
